import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
};

const services = [
  {
    num: '01',
    title: 'Social Media Strategy & Management',
    description: [
      'Social media should not feel reactive.',
      'It should feel planned, intentional, and aligned with the larger vision of your brand.',
      'Our strategy and management services provide both the thinking behind the content and the execution required to maintain consistency.',
      'We develop a clear roadmap for how your brand shows up online, ensuring your audience experiences a polished, cohesive identity across platforms.',
      'This service is ideal for brands seeking ongoing guidance, monthly planning, platform management, and elevated content direction.',
    ],
    includes: [
      'Brand & social media audit', 'Platform-specific strategy', 'Monthly content calendars',
      'Caption and messaging direction', 'Scheduling & publishing oversight', 'Content recommendations',
      'Storytelling alignment', 'Audience engagement guidance', 'Analytics review & optimization',
    ],
  },
  {
    num: '02',
    title: 'Creative Direction & Content Strategy',
    description: [
      'Creative direction defines how your brand feels.',
      'It establishes visual language, storytelling structure, and aesthetic consistency.',
      'We help brands refine their identity by creating a clear creative framework that informs content, campaigns, photography, styling, and overall presentation.',
      'Rather than creating disconnected visuals, we establish systems that bring cohesion to your content.',
      'This service is ideal for rebrands, launches, visual refreshes, or brands seeking stronger creative clarity.',
    ],
    includes: [
      'Creative concept development', 'Visual identity refinement', 'Moodboards & styling references',
      'Campaign ideation', 'Storytelling frameworks', 'Brand voice direction',
      'Content pillars', 'Feed flow recommendations', 'Aesthetic alignment',
    ],
  },
  {
    num: '03',
    title: 'Photography & Visual Production',
    description: [
      'Luxury visuals require more than good lighting.',
      'They require intention.',
      'Our photography and production services are designed to create campaign-worthy imagery that supports your brand across social media, websites, launches, PR, and digital storytelling.',
      'We approach every shoot with strategic direction — ensuring imagery feels elevated, editorial, and aligned with your positioning.',
    ],
    includes: [
      'Editorial photography', 'Founder imagery', 'Lifestyle campaigns',
      'Product storytelling', 'Brand campaigns', 'Creative shoot direction',
      'Social-first image capture', 'Location guidance', 'Styling support',
    ],
  },
  {
    num: '04',
    title: 'Event Coverage & Luxury Experiences',
    description: [
      'Moments matter.',
      'Whether hosting a launch, activation, retreat, dinner, influencer event, or luxury experience, how the event is documented becomes part of the brand story.',
      'Muse captures events with a storytelling-first approach.',
      'Rather than simply documenting what happened, we curate how the experience is remembered.',
      'We focus on atmosphere, details, energy, audience interaction, and visual storytelling.',
    ],
    includes: [
      'Event photography', 'Real-time social capture', 'Brand activations',
      'Influencer coverage', 'Behind-the-scenes storytelling', 'Highlight recap content',
      'Luxury hospitality experiences', 'Branded social moments',
    ],
  },
  {
    num: '05',
    title: 'VIP Content Days',
    description: [
      'VIP Content Days are immersive production experiences designed to create a high volume of premium assets within a single shoot.',
      'This service is built for founders, brands, and businesses seeking elevated content without the complexity of coordinating multiple shoots.',
      'We handle strategy, planning, direction, styling guidance, production, and delivery — allowing you to walk away with an extensive content library.',
    ],
    includes: [
      'Strategy session', 'Creative planning', 'Shot list development',
      'Full-day production', 'Photo & video capture', 'Styling guidance',
      'Multiple looks & environments', 'Edited deliverables', 'Social-ready content',
    ],
  },
  {
    num: '06',
    title: 'VIP Consulting',
    description: [
      'VIP Consulting offers private strategic support for founders, creatives, and brands seeking guidance.',
      'These sessions are designed to provide clarity around positioning, content direction, audience perception, launches, brand identity, or growth opportunities.',
      'Consulting is ideal for businesses not ready for full management but seeking expert direction.',
    ],
    includes: [
      'Brand review', 'Social audit', 'Strategy recommendations',
      'Positioning refinement', 'Content feedback', 'Growth opportunities',
      'Launch planning', 'Personalized roadmap',
    ],
  },
  {
    num: '07',
    title: 'Creator Partnerships & Social Amplification',
    description: [
      'As an extension of select campaigns, launches, content experiences, and brand activations, Muse also offers curated creator partnership support designed to enhance digital visibility and social reach through aligned storytelling and intentional brand placement.',
      'Rather than traditional influencer marketing, creator collaborations are approached through the lens of aesthetic alignment, audience relevance, and cohesive campaign integration.',
      'This offering is designed to support the overall creative direction and social presence of a brand while extending campaign visibility through thoughtfully selected creator partnerships.',
    ],
    includes: [
      'Creator sourcing & recommendations', 'Influencer outreach support', 'Creator gifting coordination',
      'Wellness & lifestyle creator partnerships', 'UGC sourcing opportunities', 'Campaign collaboration support',
      'Social amplification support', 'Creator coordination for launches & activations',
    ],
  },
];

export default function ServicesPage() {
  return (
    <div className="pt-44" data-testid="services-page">
      {/* Hero */}
      <section className="py-16 md:py-24" data-testid="services-hero">
        <div className="container-main">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl mx-auto text-center">
            <motion.p variants={fadeInUp} className="text-sm tracking-widest uppercase text-muted-foreground mb-4">What We Offer</motion.p>
            <motion.h1 variants={fadeInUp} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight mb-10">
              Curated Services Designed To Build Brand Presence.
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed mb-4">
              Muse Social Creative offers a curated suite of services designed to support brands across strategy, creative direction, execution, and long-term growth.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed mb-4">
              Our services are intentionally structured to work independently or as part of a larger creative partnership.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Some clients come to us for clarity. Others come for execution. Many come for both. We support brands through every stage of their visual and digital evolution.
            </motion.p>

            <motion.div variants={fadeInUp} className="mt-12">
              <img src="/images/services-hero-tray.png" alt="Be the Muse" className="w-full max-w-md mx-auto h-auto object-contain" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* Services List */}
      <section className="py-16 md:py-24" data-testid="services-list">
        <div className="container-main">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="divide-y divide-border">
            {services.map((service, i) => (
              <motion.div key={i} variants={fadeInUp} className="py-14 first:pt-0 last:pb-0" data-testid={`service-${i}`}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                  <div className="lg:col-span-1">
                    <span className="font-serif text-4xl text-butter/40 font-medium">{service.num}</span>
                  </div>

                  <div className="lg:col-span-5">
                    <h2 className="font-serif text-2xl md:text-3xl font-medium mb-6">{service.title}</h2>
                    <div className="space-y-4">
                      {service.description.map((p, j) => (
                        <p key={j} className="text-muted-foreground leading-relaxed">{p}</p>
                      ))}
                    </div>
                  </div>

                  <div className="lg:col-span-6">
                    <p className="text-sm tracking-widest uppercase text-muted-foreground mb-6">Includes</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                      {service.includes.map((item, j) => (
                        <div key={j} className="flex items-center gap-3">
                          <span className="w-1.5 h-1.5 rounded-full bg-butter flex-shrink-0" />
                          <span className="text-foreground text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* In Motion video after Photography & Visual Production */}
                {i === 2 && (
                  <div className="mt-14 pt-20 border-t border-border" data-testid="in-motion-section">
                    <div className="text-center mb-10">
                      <p className="text-sm tracking-widest uppercase text-muted-foreground mb-4">Designed for presence, performance, and impact.</p>
                      <h2 className="font-serif text-3xl md:text-4xl font-medium">In Motion</h2>
                    </div>
                    <div className="max-w-4xl mx-auto">
                      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                        <video
                          className="absolute inset-0 w-full h-full object-cover"
                          controls
                          loop
                          playsInline
                          preload="metadata"
                          src={`/images/showreel.mp4#t=7`}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-foreground" data-testid="services-cta">
        <div className="container-main text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-tight text-background mb-6">
              Not sure where to begin?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-background/60 text-base md:text-lg leading-relaxed mb-10 max-w-xl mx-auto">
              Book a discovery call and we'll help you find the right approach for your brand.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/book-a-call" className="inline-flex items-center gap-2 bg-butter text-foreground font-medium text-sm px-8 py-3.5 rounded-full hover:bg-butter-dark transition-colors duration-300" data-testid="services-book-call-btn">
                Book a Strategy Call <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
